import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { classNames } from '../utils/classNames';
import { formatSigned, formatValue, NO_VALUE, ValueFormat } from './chartFormat';

// DataTableMatrix: a table of blocks × measures × columns, with the look of DataTable. Each block (for example one
// property, a subtotal or the total) has one row per measure, and each MEASURE row has its own number format (money,
// number, percent, signed, a change tone), which DataTable cannot do (its format is per column). The first two columns
// (the block label and the measure label) stay in place on a sideways scroll. A value of 0 or a missing value shows "–".
// It knows nothing about RoomPulse data: the caller gives the labels, the formats, the values and each total.
// With stickyHeader (from the sm width, 640 px), the header row stays at the top of the window while the page scrolls down,
// until the last row passes, by the method of DataTable's stickyHeader: the header in view is a copy for the eye and the
// pointer (aria-hidden), and the real header stays in the table under it, for a screen reader and the keyboard. The copy takes
// the widths of the real header cells and re-measures when they change, also when new values change the column widths at
// the same table width. A table wider than its frame gets a drawn second scrollbar under the copy; it, the copy and the
// table move together. Below the sm width (a phone) the table keeps its plain header.

export type DataTableMatrixMeasure = {
	id: string,
	label: string,
	// The label below the sm width (640 px), when the measure column is narrow. Default: label.
	shortLabel?: string,
	format: ValueFormat,
	currency?: string,
	decimals?: number,
	// + before a positive value.
	signed?: boolean,
	// 'change': a positive value in green-700, a negative one in red-700 (as the DataTable tone 'change').
	tone?: 'change',
};

export type DataTableMatrixValues = { cells: (number | null)[], total: number | null };

export type DataTableMatrixBlock = {
	id: string,
	label: string,
	// A second line under the label, for example "Subtotal · 4 properties".
	sublabel?: string,
	// 'total' and 'subtotal' get the emphasis ground and bold figures; 'item' is a plain block. Default 'item'.
	kind?: 'total' | 'subtotal' | 'item',
	// The values of each measure, by measure id: one value per column, in the order of `columns`, and the total.
	values: Record<string, DataTableMatrixValues>,
};

export type DataTableMatrixColumn = { id: string, label: string };

type Props = {
	ariaLabel: string,
	// The header of the two frozen columns, for example ['Property', 'Measure'].
	frozenLabels: [string, string],
	columns: DataTableMatrixColumn[],
	totalLabel?: string,
	measures: DataTableMatrixMeasure[],
	blocks: DataTableMatrixBlock[],
	// compact: narrow frozen columns and the short measure labels (a phone). Default false.
	compact?: boolean,
	// The header row stays at the top of the window while the page scrolls (from the sm width). Default false.
	stickyHeader?: boolean,
};

// stickyHeader: from the sm width (640 px); a phone keeps the plain header. THUMB_MIN: the narrowest thumb of the drawn bar.
// SCROLLBAR: the scrollbar under the last row has the look of the drawn second scrollbar (a WebKit style; Firefox keeps its own).
const SM_UP = '(min-width: 640px)';
const THUMB_MIN = 32;
const SCROLLBAR = '[&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 hover:[&::-webkit-scrollbar-thumb]:bg-gray-400';

const FILL: Record<NonNullable<DataTableMatrixBlock['kind']>, string> = { total: 'bg-gray-100', subtotal: 'bg-gray-50', item: 'bg-white' };

const textOf = (value: number | null, measure: DataTableMatrixMeasure): { text: string, tone: string } => {
	if (value === null || Number.isNaN(value) || Math.abs(value) < 1e-9) return { text: NO_VALUE, tone: 'text-gray-300' };
	const options = { currency: measure.currency, decimals: measure.decimals };
	const text = measure.signed === true ? formatSigned(value, measure.format, options) : formatValue(value, measure.format, options);
	const tone = measure.tone === 'change' ? (value > 0 ? 'font-medium text-green-700' : 'font-medium text-red-700') : 'text-gray-700';
	return { text, tone };
};

const DataTableMatrix: React.FunctionComponent<Props> = ({ ariaLabel, frozenLabels, columns, totalLabel = 'Total', measures, blocks, compact = false, stickyHeader = false }) => {
	// stickyHeader: the header copy at the top of the window (from the sm width; a phone keeps the plain header).
	const [smUp, setSmUp] = useState(() => stickyHeader && typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(SM_UP).matches);
	useEffect(() => {
		if (!stickyHeader) return undefined;
		const query = window.matchMedia(SM_UP);
		const change = () => setSmUp(query.matches);
		change();
		query.addEventListener('change', change);
		return () => query.removeEventListener('change', change);
	}, [stickyHeader]);
	const pinned = stickyHeader && smUp;
	const scrollBox = useRef<HTMLDivElement>(null);
	const realTable = useRef<HTMLTableElement>(null);
	const realHead = useRef<HTMLTableSectionElement>(null);
	const copyHead = useRef<HTMLDivElement>(null);
	const topBar = useRef<HTMLDivElement>(null);
	const thumb = useRef<HTMLDivElement>(null);
	const thumbDrag = useRef<{ x: number, left: number } | undefined>(undefined);
	// The measures of the real table that the copy follows: the width of each header cell, the table width, the header
	// height, and the scroll width of the frame (a second scrollbar only when the table is wider than the frame).
	const [fit, setFit] = useState<{ widths: number[], table: number, head: number, scroll: number, frame: number } | null>(null);
	useLayoutEffect(() => {
		if (!pinned || realTable.current === null || realHead.current === null || scrollBox.current === null) {
			setFit(null);
			return undefined;
		}
		const table = realTable.current, head = realHead.current, box = scrollBox.current;
		const cells = [...(head.rows[0]?.cells ?? [])];
		const measure = () => {
			const next = {
				widths: cells.map(cell => cell.getBoundingClientRect().width),
				table: table.getBoundingClientRect().width,
				head: head.getBoundingClientRect().height,
				scroll: box.scrollWidth,
				frame: box.clientWidth,
			};
			setFit(current => current !== null && current.table === next.table && current.head === next.head
				&& current.scroll === next.scroll && current.frame === next.frame
				&& current.widths.length === next.widths.length && current.widths.every((w, i) => w === next.widths[i]) ? current : next);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(table);
		observer.observe(head);
		observer.observe(box);
		// Each header cell too: new values can change the column widths while the table keeps its width (ROO2-300).
		cells.forEach(cell => observer.observe(cell));
		return () => observer.disconnect();
	}, [pinned, columns, compact]);
	const wide = fit !== null && fit.scroll > fit.frame + 1;
	// The second scrollbar is drawn (a track and a thumb), as in DataTable: a Mac hides a native scrollbar until a scroll.
	// The thumb width = the share of the table in view.
	const thumbGeometry = () => {
		const track = topBar.current?.clientWidth ?? 0;
		if (fit === null || track === 0) return { width: 0, travel: 0, range: 0 };
		const width = Math.max(THUMB_MIN, Math.round(track * fit.frame / fit.scroll));
		return { width, travel: track - width, range: fit.scroll - fit.frame };
	};
	const placeThumb = (left: number) => {
		if (thumb.current === null) return;
		const { width, travel, range } = thumbGeometry();
		thumb.current.style.width = `${width}px`;
		thumb.current.style.transform = `translateX(${range > 0 ? Math.round(left / range * travel) : 0}px)`;
	};
	// The table, the header copy and the second scrollbar follow the one that the user moves.
	const follow = (left: number) => {
		const box = scrollBox.current;
		if (box === null) return;
		if (box.scrollLeft !== left) box.scrollLeft = left;
		const shown = box.scrollLeft;
		if (copyHead.current !== null && copyHead.current.scrollLeft !== shown) copyHead.current.scrollLeft = shown;
		placeThumb(shown);
	};
	useLayoutEffect(() => { if (fit !== null && scrollBox.current !== null) follow(scrollBox.current.scrollLeft); }, [fit, wide]);
	// A drag of the thumb moves the table by the same share; a press on the track beside the thumb moves it by one frame.
	const onThumbDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0 || scrollBox.current === null) return;
		event.preventDefault();
		event.stopPropagation();
		event.currentTarget.setPointerCapture(event.pointerId);
		thumbDrag.current = { x: event.clientX, left: scrollBox.current.scrollLeft };
	};
	const onThumbMove = (event: React.PointerEvent<HTMLDivElement>) => {
		const drag = thumbDrag.current;
		if (drag === undefined) return;
		const { travel, range } = thumbGeometry();
		if (travel > 0) follow(drag.left + (event.clientX - drag.x) * range / travel);
	};
	const onThumbUp = (event: React.PointerEvent<HTMLDivElement>) => {
		thumbDrag.current = undefined;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
	};
	const onTrackDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0 || scrollBox.current === null || thumb.current === null || fit === null) return;
		const before = event.clientX < thumb.current.getBoundingClientRect().left;
		follow(scrollBox.current.scrollLeft + (before ? -fit.frame : fit.frame));
	};
	// A sideways wheel or swipe on the header copy moves the table (the copy itself has no scrollbar).
	const onCopyWheel = (event: React.WheelEvent) => {
		const dx = event.deltaX !== 0 ? event.deltaX : (event.shiftKey ? event.deltaY : 0);
		if (dx !== 0 && scrollBox.current !== null) follow(scrollBox.current.scrollLeft + dx);
	};

	// The two frozen columns get their width from a box of a fixed width inside the cell (the cell has no padding): a table
	// cell with a width can still shrink, and then the second column, pinned at the width of the first, would overlap it.
	const w1 = compact ? 'w-28' : 'w-40';
	const left2 = compact ? 'left-28' : 'left-40';
	const w2 = compact ? 'w-14' : 'w-24';
	const head = 'whitespace-nowrap border-b border-gray-300 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500';
	// The header row: in the table, and (stickyHeader) once more in the copy at the top of the window.
	const headRow = <tr>
		<th scope="col" className={classNames(head, 'sticky left-0 z-20 bg-white p-0 text-left')}><div className={classNames(w1, compact ? 'pl-3 pr-2' : 'pl-4 pr-3 sm:pl-6')}>{frozenLabels[0]}</div></th>
		<th scope="col" className={classNames(head, 'sticky z-20 border-r bg-white p-0 text-left', left2)}><div className={classNames(w2, 'px-2')}>{compact ? '' : frozenLabels[1]}</div></th>
		{columns.map(c => <th key={c.id} scope="col" className={classNames(head, 'w-16 px-1.5 text-right')}>{c.label}</th>)}
		<th scope="col" className={classNames(head, 'w-20 border-l px-3 text-right')}>{totalLabel}</th>
	</tr>;
	// stickyHeader: the frame clips (`overflow-clip`) and does not scroll, so the header copy sticks to the window.
	return <div className={classNames('rounded-lg border border-gray-200 bg-white shadow-sm', pinned ? 'overflow-clip' : 'overflow-hidden')}>
		{/* The copy (z-20) lies over the table box (z-0: its own stacking layer, so no cell of the table can come above it). */}
		{pinned && fit !== null && <div aria-hidden="true" data-matrix-copy="" className="sticky top-0 z-20 bg-white">
			<div ref={copyHead} onWheel={onCopyWheel} className="overflow-hidden bg-white">
				<table className="table-fixed border-separate border-spacing-0" style={{ width: fit.table }}>
					<colgroup>{fit.widths.map((width, i) => <col key={i} style={{ width }} />)}</colgroup>
					<thead>{headRow}</thead>
				</table>
			</div>
			{wide && <div ref={topBar} onPointerDown={onTrackDown} onWheel={onCopyWheel} className="relative h-2.5 bg-gray-100">
				<div ref={thumb} onPointerDown={onThumbDown} onPointerMove={onThumbMove} onPointerUp={onThumbUp} onPointerCancel={onThumbUp}
					className="absolute inset-y-0 left-0 touch-none rounded-full bg-gray-300 hover:bg-gray-400 active:bg-gray-400" />
			</div>}
		</div>}
		{/* stickyHeader: the real header slides under the copy, so the first row starts right under the copy. */}
		<div ref={scrollBox} onScroll={pinned ? event => follow(event.currentTarget.scrollLeft) : undefined}
			className={classNames('relative overflow-x-auto', pinned ? 'z-0' : '', pinned && wide ? SCROLLBAR : '')}
			style={pinned && fit !== null ? { marginTop: -fit.head } : undefined}>
			<table ref={realTable} className="min-w-full border-separate border-spacing-0">
				<caption className="sr-only">{ariaLabel}</caption>
				<thead ref={realHead}>{headRow}</thead>
				<tbody>
					{blocks.flatMap(block => {
						const kind = block.kind ?? 'item';
						const strong = kind !== 'item';
						const fill = FILL[kind];
						return measures.map((measure, i) => {
							const last = i === measures.length - 1;
							const edge = last ? (strong ? 'border-b border-gray-300' : 'border-b border-gray-200') : '';
							const values = block.values[measure.id] ?? { cells: [], total: null };
							const total = textOf(values.total, measure);
							return <tr key={`${block.id}|${measure.id}`} className={fill}>
								{i === 0 && <th scope="rowgroup" rowSpan={measures.length} className={classNames('sticky left-0 z-10 p-0 text-left align-top text-xs text-gray-900', fill,
									strong ? 'border-b border-gray-300 font-semibold' : 'border-b border-gray-200 font-medium')}>
									<div className={classNames(w1, 'py-2', compact ? 'pl-3 pr-2' : 'pl-4 pr-3 sm:pl-6')}>
										<span className="block">{block.label}</span>
										{block.sublabel !== undefined && <span className="block font-normal text-gray-500">{block.sublabel}</span>}
									</div>
								</th>}
								<td className={classNames('sticky z-10 whitespace-nowrap border-r border-gray-300 p-0 text-xs', left2, fill, edge, strong ? 'font-semibold text-gray-700' : 'text-gray-500')}>
									<div className={classNames(w2, 'px-2 py-1.5')}>{compact ? measure.shortLabel ?? measure.label : measure.label}</div>
								</td>
								{columns.map((c, j) => {
									const v = textOf(values.cells[j] ?? null, measure);
									return <td key={c.id} className={classNames('whitespace-nowrap px-1.5 py-1.5 text-right text-xs tabular-nums', edge, v.tone, strong && v.text !== NO_VALUE ? 'font-semibold' : '')}>{v.text}</td>;
								})}
								<td className={classNames('whitespace-nowrap border-l border-gray-200 px-3 py-1.5 text-right text-xs font-semibold tabular-nums', edge, total.tone === 'text-gray-700' ? 'text-gray-900' : total.tone)}>{total.text}</td>
							</tr>;
						});
					})}
				</tbody>
			</table>
		</div>
	</div>;
};

export default DataTableMatrix;
